import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { backTen, forwardTen } from '../../../../helpers/historyHelper.ts'
import dataStore from '../../../../store/dataStore.ts'
import historyStore from '../../../../store/historyStore.ts'
import { IPet, IPoint } from '../../../../interfaces/types.ts'
import { centerMap } from '../../../map/mapHelpers.tsx'
import { DateButton } from '../DateButton.tsx'
import { HistoryListItem } from './HistoryListItem.tsx'
import { SVGSkeleton, Skeleton } from './Skeleton.tsx'
import { HistoryToggles } from '../HistoryToggles.tsx'

interface Props {
    listRef?: any
}

export const HistoryList: React.FC<Props> = (props) => {
    const locations = historyStore(s => s.locations)
    const selectedPetId = historyStore(s => s.selectedPetId)
    const pets = dataStore(s => s.pets)
    const pet = pets.find(p => p._id === selectedPetId)
    const reachedStart = historyStore(s => s.reachedStart)
    const startDate = historyStore(s => s.startDate)
    const endDate = historyStore(s => s.endDate)
    const navigate = useNavigate()
    
    const generateLocationList = (locations: IPoint[], pet: IPet | undefined) => {
        if (!pet) return <div/>
        return locations.map((location, index) => {
            return <HistoryListItem
                key={location._id}
                location={location}
                pet={pet}
                index={index}
                centerMap={centerMap}
                flex={locations.length < 10 ? 'flex-0.25' : 'flex-1'}
                desktop
            />
        })
    }

    const noHistory = (
        <>
            <div className="relative p-1 border-2 border-solid flex-1 w-[10vw]" style={{lineHeight: '0.75rem'}}>
                <div className="w-4 h-4 absolute bottom-1 left-0.5">
                    <SVGSkeleton className="w-4 h-4" />
                </div>
                <div className="flex flex-col justify-center gap-0">
                    <div className="self-center">
                        <Skeleton className="w-[64px] max-w-full" />
                    </div>
                    <div className={'self-center'}>
                        <Skeleton className="w-[96px] max-w-full" />
                    </div>
                    <div className={'self-center'}>
                        <Skeleton className="w-[64px] max-w-full" />
                    </div>
                </div>
            </div>
        </>
    )

    const list = locations?.length ?
        <div className={'flex flex-row justify-between items-center p-2 pb-0 pr-0 w-full min-h-[64px]'} ref={props.listRef}>
            <div className={'flex flex-row justify-start items-center gap-2 w-full overflow-x-auto bg-base-100 history-scrollbar'}>
                {/*{lists[listIndex] ? lists[listIndex] : noHistory}*/}
                {generateLocationList(locations, pet)}
            </div>
            {/*{listControl}*/}
        </div>
        :
        <div className={'flex flex-col justify-start items-center p-2 pb-0 w-full min-h-[64px] relative'} ref={props.listRef}>
            <div className={'flex flex-row justify-start items-center gap-2 w-full overflow-x-auto bg-base-100 history-scrollbar'}>
                <div className={'w-fit self-center bg-white p-2 flex flex-row justify-start items-center gap-2 w-full overflow-hidden'}>
                    {noHistory} {noHistory} {noHistory}{noHistory} {noHistory} {noHistory}{noHistory} {noHistory} {noHistory}
                </div>
            </div>
            <div className={'w-full absolute top-6 left-0'}>
                <div className={'flex justify-center w-full'}>
                    <div className={'bg-white rounded p-1 text-lg text-accent w-fit text-center'}>
                        No history available
                    </div>
                </div>
            </div>
        </div>
    
    const left = (
        <DateButton direction={'newer'}
                    reachedStart={reachedStart}
                    historyAvailable={locations.length}
                    backwardTen={async (e) => {
                        const stateUpdate = await backTen(e)
                        historyStore.getState().setStateUpdate(stateUpdate)
                    }}
                    forwardTen={async (e) => {
                        const stateUpdate = await forwardTen(e)
                        historyStore.getState().setStateUpdate(stateUpdate)
                    }}
                    selectingDate={false}
                    date={startDate}
        />
    )
    
    const right = (
        <DateButton direction={'older'}
                    reachedStart={reachedStart}
                    historyAvailable={locations.length}
                    backwardTen={async (e) => {
                        const stateUpdate = await backTen(e)
                        historyStore.getState().setStateUpdate(stateUpdate)
                    }}
                    forwardTen={async (e) => {
                        const stateUpdate = await forwardTen(e)
                        historyStore.getState().setStateUpdate(stateUpdate)
                    }}
                    selectingDate={false}
                    date={endDate}
        />
    )
    
    const headerRow = (
        <div className="flex items-center justify-between px-3 pt-2 pb-1">
            <button
                className="btn btn-sm btn-ghost text-primary gap-1"
                onClick={() => navigate('/map')}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Map
            </button>
            <div className="text-xl text-primary font-bold">
                {pet?.petName}
            </div>
            <HistoryToggles />
        </div>
    )
    
    const container = (
        <div className={'relative w-full h-fit'}>
            {headerRow}
            <div className={'absolute top-12 left-0 pl-1'} style={{zIndex: 1}}>
                {left}
            </div>
            <div className={'ml-[50px] mr-[50px]'}>
                {list}
            </div>
            <div className={'absolute z-1 top-12 right-0 pr-1'}>
                {right}
            </div>
        </div>
    )
    
    return container
}
